import { apiClient } from '../client';
import { FileNode, FileContent, Pipeline, PipelineSaveRequest } from '../types';
import { createContextLogger } from '../../utils/logger';

const logger = createContextLogger('FilesService');

// Type alias for workflow - using unknown until proper Workflow interface is defined
type WorkflowData = unknown;

export class FilesService {
  /**
   * Get file list structure
   */
  async getFileList(): Promise<FileNode> {
    try {
      logger.debug('Fetching file list...');
      const fileStructure = await apiClient.get<FileNode>('/v1/file/getFileList');
      logger.debug('File list fetched successfully');
      return fileStructure;
    } catch (error) {
      logger.error('Failed to fetch file list:', error);
      throw error;
    }
  }

  /**
   * Get pipeline file content
   */
  async getPipelineFile(path: string): Promise<Pipeline> {
    try {
      logger.debug('Fetching pipeline file:', path);
      const content = await apiClient.get<Pipeline>('/v1/pipeline/getPipeline', {
        params: { path }
      });
      logger.debug('Pipeline file fetched successfully');
      return content;
    } catch (error) {
      logger.error('Failed to fetch pipeline file:', error);
      throw error;
    }
  }

  /**
   * Get workflow file content
   */
  async getWorkflowFile(path: string): Promise<WorkflowData> {
    try {
      logger.debug('Fetching workflow file:', path);
      const content = await apiClient.get<WorkflowData>('/v1/file/getFileWorkflow', {
        params: { path }
      });
      logger.debug('Workflow file fetched successfully');
      return content;
    } catch (error) {
      logger.error('Failed to fetch workflow file:', error);
      throw error;
    }
  }

  /**
   * Get general file content (markdown, text, etc.)
   */
  async getFile(path: string): Promise<FileContent> {
    try {
      logger.debug('Fetching file:', path);
      const content = await apiClient.get<FileContent>('/v1/file/getFile', {
        params: { path }
      });
      logger.debug('File fetched successfully');
      return content;
    } catch (error) {
      logger.error('Failed to fetch file:', error);
      throw error;
    }
  }

  /**
   * Save pipeline structure.
   * Backend will perform smart merge to preserve existing transform values.
   * Use this for all pipeline saves: new files, structural changes, and regular saves.
   */
  async savePipelineFile(path: string, structure: import('../types').PipelineSaveRequest): Promise<void> {
    try {
      logger.debug('Saving pipeline structure:', { path, transformCount: structure.transforms.length, hopCount: structure.hops.length });
      await apiClient.post('/v1/pipeline/savePipelineStructure', structure, {
        params: { path }
      });
      logger.debug('Pipeline structure saved successfully');
    } catch (error) {
      logger.error('Failed to save pipeline structure:', error);
      throw error;
    }
  }

  /**
   * Save workflow structure.
   * Backend will perform smart merge to preserve existing action values.
   * Use this for all workflow saves: new files, structural changes, and regular saves.
   */
  async saveWorkflowFile(path: string, structure: import('../types').PipelineSaveRequest): Promise<void> {
    try {
      logger.debug('Saving workflow structure:', { path, actionCount: structure.transforms.length, hopCount: structure.hops.length });
      await apiClient.post('/v1/workflow/saveWorkflowStructure', structure, {
        params: { path }
      });
      logger.debug('Workflow structure saved successfully');
    } catch (error) {
      logger.error('Failed to save workflow structure:', error);
      throw error;
    }
  }

  /**
   * Save general file
   */
  async saveFile(path: string, content: string): Promise<void> {
    try {
      logger.debug('Saving file:', path);
      await apiClient.post('/v1/file/postFile', content, {
        params: { path }
      });
      logger.debug('File saved successfully');
    } catch (error) {
      logger.error('Failed to save file:', error);
      throw error;
    }
  }

  /**
   * Create new file
   */
  async createFile(path: string, type: 'pipeline' | 'workflow' | 'markdown' | 'text'): Promise<void> {
    try {
      logger.debug('Creating file:', { path, type });
      await apiClient.post('/v1/file/create', {
        path,
        type
      });
      logger.debug('File created successfully');
    } catch (error) {
      logger.error('Failed to create file:', error);
      throw error;
    }
  }

  /**
   * Delete file
   */
  async deleteFile(path: string): Promise<void> {
    try {
      logger.debug('Deleting file:', path);
      await apiClient.delete('/v1/file/delete', {
        params: { path }
      });
      logger.debug('File deleted successfully');
    } catch (error) {
      logger.error('Failed to delete file:', error);
      throw error;
    }
  }

  /**
   * Rename file
   */
  async renameFile(oldPath: string, newPath: string): Promise<void> {
    try {
      logger.debug('Renaming file:', { oldPath, newPath });
      await apiClient.post('/v1/file/rename', {
        oldPath,
        newPath
      });
      logger.debug('File renamed successfully');
    } catch (error) {
      logger.error('Failed to rename file:', error);
      throw error;
    }
  }

  /**
   * Check if file exists
   */
  async fileExists(path: string): Promise<boolean> {
    try {
      logger.debug('Checking if file exists:', path);
      const exists = await apiClient.get<boolean>('/v1/file/exists', {
        params: { path }
      });
      logger.debug('File existence check completed:', exists);
      return exists;
    } catch (error) {
      logger.error('Failed to check file existence:', error);
      // Return false if file doesn't exist (404) or other errors
      return false;
    }
  }

  /**
   * Get file metadata
   */
  async getFileMetadata(path: string): Promise<{ size: number; lastModified: string; type: string }> {
    try {
      logger.debug('Getting file metadata:', path);
      const metadata = await apiClient.get<{ size: number; lastModified: string; type: string }>(
        '/v1/file/metadata',
        { params: { path } }
      );
      logger.debug('File metadata fetched successfully');
      return metadata;
    } catch (error) {
      logger.error('Failed to get file metadata:', error);
      throw error;
    }
  }

  /**
   * Search files by name or content
   */
  async searchFiles(query: string, searchContent: boolean = false): Promise<FileNode[]> {
    try {
      logger.debug('Searching files:', { query, searchContent });
      const results = await apiClient.get<FileNode[]>('/v1/file/search', {
        params: { query, searchContent }
      });
      logger.debug('File search completed:', results.length, 'results');
      return results;
    } catch (error) {
      logger.error('Failed to search files:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const filesService = new FilesService(); 